// Keeping the browser's analytics choice and the server's record of it in agreement.
//
// Wave C item C1.10, closing finding F16. Two stores exist and neither can be dropped:
//
//   - localStorage (utils/cookieConsent.js) is the MECHANISM. GA4 loads per browser, so
//     the decision has to be readable where the script tag is.
//   - `user_consents.analytics` is the RECORD. It is what a DPDP export can state, what
//     an auditor can be shown, and what makes s11(1)(c)'s "who was your data shared with"
//     answerable at all. Before C1.10 it did not exist, so the export carried a caveat
//     admitting we could not say whether our only cross-border transfer applied to the
//     person asking.
//
// THE RECONCILIATION RULE: the most recent decision wins, wherever it was made.
//
// The tempting rule is "the server is authoritative", and it is wrong in a case that
// really happens: sign out, click "Necessary Only" on the landing page, sign back in.
// That click is an explicit, recent act of will; a server row from last month is a
// memory of an older one. Overriding the former with the latter would quietly re-enable
// analytics for someone who had just turned it off, which is precisely the kind of thing
// this whole wave exists to stop. Comparing timestamps is barely more code and is the
// only rule that respects the user in both directions.
import { getConsents, grantConsent, withdrawConsent } from './consentApi';
import { analyticsEnabled, readCookiePreferences, setAnalyticsPreference } from './cookieConsent';

const ANALYTICS_PURPOSE = 'analytics';
const BANNER_SOURCE = 'cookie_banner';

const GRANTED = 'granted';
const WITHDRAWN = 'withdrawn';

// Push one decision up. Used straight after a banner save, and by the reconcile below
// when the browser holds the newer decision.
const writeAnalyticsConsent = (enabled) => (enabled
  ? grantConsent(ANALYTICS_PURPOSE, BANNER_SOURCE)
  : withdrawConsent(ANALYTICS_PURPOSE, BANNER_SOURCE));

// Called when the banner is saved by a signed-in user. Deliberately swallows failures:
// the choice has already taken effect in this browser, and a network error must not be
// surfaced as "your consent did not save" when the thing the user asked for - analytics
// on or off, right now - did in fact happen. The next reconcile picks it up.
export async function pushAnalyticsConsent(preferences) {
  try {
    await writeAnalyticsConsent(analyticsEnabled(preferences));
  } catch (error) {
    console.error('Failed to record the analytics choice on the server:', error);
  }
}

// Milliseconds, from whichever side. The server stores unix SECONDS, the browser an ISO
// string; comparing them without this is how you get a 1970-vs-2026 bug that always
// picks the same winner.
const serverDecidedAtMs = (consent) => {
  const seconds = consent?.state === GRANTED ? consent.grantedAt : consent.withdrawnAt;
  return Number.isFinite(seconds) ? seconds * 1000 : null;
};

const localDecidedAtMs = (preferences) => {
  const parsed = Date.parse(preferences?.consentedAt ?? '');
  return Number.isNaN(parsed) ? null : parsed;
};

// Run once per signed-in session. Returns what it did, which is only used by tests and
// by anyone reading a console trace - callers ignore it.
export async function reconcileAnalyticsConsent() {
  let consent;
  try {
    const res = await getConsents();
    if (!res?.success) return 'unavailable';
    consent = (res.consents || []).find((c) => c.purpose === ANALYTICS_PURPOSE);
  } catch (error) {
    console.error('Failed to read stored consents:', error);
    return 'unavailable';
  }

  const local = readCookiePreferences();
  const serverDecided = consent?.state === GRANTED || consent?.state === WITHDRAWN;

  // Neither side has an answer: the banner is still up and the user has not chosen. Doing
  // anything here would be inventing a decision on their behalf.
  if (!serverDecided && !local) return 'no-decision';

  // Only the browser has one - the usual case for an account that predates C1.10, or one
  // whose owner answered the banner before ever signing in. Their choice becomes evidence.
  if (!serverDecided) {
    await pushAnalyticsConsent(local);
    return 'pushed';
  }

  const serverEnabled = consent.state === GRANTED;

  // Only the server has one: a decision made on another device. Adopt it, so the same
  // account behaves the same way everywhere.
  if (!local) {
    setAnalyticsPreference(serverEnabled);
    return 'adopted';
  }

  if (analyticsEnabled(local) === serverEnabled) return 'agreed';

  // They disagree, so the newer one wins. A missing timestamp on either side is treated
  // as older than a present one; if neither has one, the server's is preferred, since it
  // is the copy that can actually be produced as evidence.
  const localAt = localDecidedAtMs(local);
  const serverAt = serverDecidedAtMs(consent);

  if (localAt !== null && (serverAt === null || localAt > serverAt)) {
    await pushAnalyticsConsent(local);
    return 'pushed';
  }

  setAnalyticsPreference(serverEnabled);
  return 'adopted';
}
